import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { dailyCategoriesAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Trophy, Target, Zap, Clock, TrendingUp, TrendingDown, 
  Minus, Folder, BarChart3, Calendar, Star, CheckCircle2, XCircle,
  Sparkles, Award, Brain, Layers, BookOpen
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

export default function DailyCategoriesStats() {
  useSEO('Category Stats | JepPrep', 'Track your performance across trivia categories and identify areas for improvement.');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dailyCategoriesAPI.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load statistics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatPlayTime = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'text-emerald-400';
    if (accuracy >= 60) return 'text-yellow-300';
    if (accuracy >= 40) return 'text-amber-400';
    return 'text-rose-400';
  };

  const getMasteryLevel = (accuracy, total) => {
    if (total < 5) return { level: 'Novice', color: 'bg-slate-500' };
    if (accuracy >= 90) return { level: 'Master', color: 'bg-purple-500' };
    if (accuracy >= 75) return { level: 'Expert', color: 'bg-sky-500' };
    if (accuracy >= 60) return { level: 'Proficient', color: 'bg-emerald-500' };
    if (accuracy >= 40) return { level: 'Learning', color: 'bg-yellow-500' };
    return { level: 'Beginner', color: 'bg-orange-500' };
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'easy': return 'text-green-400 bg-green-500/20';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20';
      case 'hard': return 'text-red-400 bg-red-500/20';
      default: return 'text-purple-400 bg-purple-500/20';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-white text-xl">Loading statistics...</div>
      </div>
    );
  }

  if (!stats || !stats.has_data) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <Card className="p-8 text-center bg-[hsl(var(--secondary))] border-[hsl(var(--border))] max-w-md">
          <Folder className="w-16 h-16 mx-auto text-violet-400 mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">No Statistics Yet</h2>
          <p className="text-gray-400 mb-6">Complete your first Daily Categories session to see your statistics.</p>
          <Link to="/daily-categories">
            <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-semibold hover:bg-[hsl(var(--brand-blue-800))] hover:text-white" data-testid="start-training-empty-btn">
              <Folder className="w-4 h-4 mr-2" />
              Start Training
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Header */}
      <header className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 shrink-0">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12 shrink-0" />
              <h1 className="text-lg sm:text-xl font-display text-white hidden sm:block">Daily Categories Statistics</h1>
            </div>
            <Link to="/daily-categories">
              <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-semibold hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-sm shrink-0" data-testid="new-session-btn">
                <Folder className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Session</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Cards - Blue background like Sprint */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Target className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--brand-gold-500))]" />
              <p className="text-3xl font-bold text-white" data-testid="stat-sessions">{stats.overview.total_sessions}</p>
              <p className="text-gray-400 text-sm">Sessions</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Brain className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--brand-gold-500))]" />
              <p className={`text-3xl font-bold ${getAccuracyColor(stats.overview.overall_accuracy)}`} data-testid="stat-accuracy">
                {stats.overview.overall_accuracy}%
              </p>
              <p className="text-gray-400 text-sm">Accuracy</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Layers className="w-8 h-8 mx-auto mb-2 text-orange-400" />
              <p className="text-3xl font-bold text-white" data-testid="stat-categories">{stats.overview.categories_trained}</p>
              <p className="text-gray-400 text-sm">Categories</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--brand-gold-500))]" />
              <p className="text-3xl font-bold text-white" data-testid="stat-time">{formatPlayTime(stats.overview.total_time_seconds)}</p>
              <p className="text-gray-400 text-sm">Time Played</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] p-1 w-full flex">
            <TabsTrigger 
              value="overview" 
              className="flex-1 text-gray-300 data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))] font-medium text-xs sm:text-sm"
            >
              <BarChart3 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              className="flex-1 text-gray-300 data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))] font-medium text-xs sm:text-sm"
            >
              <Calendar className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sessions</span>
            </TabsTrigger>
            <TabsTrigger 
              value="review" 
              className="flex-1 text-gray-300 data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))] font-medium text-xs sm:text-sm"
            >
              <BookOpen className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Review</span>
            </TabsTrigger>
            <TabsTrigger 
              value="categories" 
              className="flex-1 text-gray-300 data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))] font-medium text-xs sm:text-sm"
            >
              <Brain className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Mastery</span>
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Recent Performance */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Minus className="w-5 h-5 text-slate-400" />
                    Recent Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.recent_sessions && stats.recent_sessions.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent_sessions.slice(0, 7).map((session, idx) => (
                        <div key={session.completed_at || `session-${idx}`} className="flex items-center gap-3">
                          <div className="w-16 text-sm text-gray-300">
                            {formatDate(session.completed_at)}
                          </div>
                          <div className="flex-1 h-2 rounded-full overflow-hidden bg-[hsl(var(--accent))]">
                            <div 
                              className="h-full rounded-full transition-all" 
                              style={{ 
                                width: `${session.accuracy}%`,
                                backgroundColor: session.accuracy >= 60 ? '#34d399' : session.accuracy >= 40 ? '#fbbf24' : '#fb7185'
                              }} 
                            />
                          </div>
                          <div className={`w-12 text-right font-mono ${getAccuracyColor(session.accuracy)}`}>
                            {session.accuracy}%
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-300">No recent sessions</p>
                  )}
                </CardContent>
              </Card>

              {/* Favorite & Records */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Star className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                    Highlights
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Favorite Category</span>
                    <span className="text-[hsl(var(--brand-gold-400))] font-bold truncate max-w-[180px]">{stats.overview.favorite_category || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Best Accuracy</span>
                    <span className="text-emerald-400 font-bold">
                      {stats.recent_sessions && stats.recent_sessions.length > 0 
                        ? Math.max(...stats.recent_sessions.map(s => s.accuracy)) + '%'
                        : 'N/A'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Total Questions</span>
                    <span className="text-white font-mono">{stats.overview.total_questions?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Categories Trained</span>
                    <span className="text-white font-mono">{stats.overview.categories_trained}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Difficulty Breakdown */}
              {stats.difficulty_breakdown && stats.difficulty_breakdown.length > 0 && (
                <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                      Difficulty Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {stats.difficulty_breakdown.map((diff) => (
                      <div key={diff.difficulty} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-200 capitalize">{diff.difficulty}</span>
                          <span className={getAccuracyColor(diff.accuracy)}>{diff.accuracy}%</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden bg-[hsl(var(--accent))]">
                          <div 
                            className="h-full rounded-full" 
                            style={{ 
                              width: `${diff.accuracy}%`,
                              backgroundColor: diff.accuracy >= 60 ? '#34d399' : diff.accuracy >= 40 ? '#fbbf24' : '#fb7185'
                            }} 
                          />
                        </div>
                        <p className="text-xs text-gray-500">{diff.correct}/{diff.total} correct</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Practice Time */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                    Training Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Total Practice Time</span>
                    <span className="text-cyan-400 font-bold text-xl">{formatPlayTime(stats.overview.total_time_seconds)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Avg per Session</span>
                    <span className="text-white font-mono">
                      {stats.overview.total_sessions > 0 
                        ? formatPlayTime(Math.round(stats.overview.total_time_seconds / stats.overview.total_sessions))
                        : '0m'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Total Sessions</span>
                    <span className="text-white font-mono">{stats.overview.total_sessions}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Overall Accuracy</span>
                    <span className={`font-bold ${getAccuracyColor(stats.overview.overall_accuracy)}`}>{stats.overview.overall_accuracy}%</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CATEGORY MASTERY TAB */}
          <TabsContent value="categories" className="space-y-4">
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Brain className="w-5 h-5 text-violet-400" />
                  Category Mastery - Mode Comparison
                </CardTitle>
                <p className="text-gray-400 text-sm">
                  Compare your performance in Daily Categories vs Sprint Mode
                </p>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-4">
                    {stats.category_mastery.map((cat, idx) => {
                      const mastery = getMasteryLevel(cat.combined.accuracy, cat.combined.total);
                      return (
                        <div 
                          key={cat.name} 
                          className="p-4 bg-[hsl(var(--accent))] rounded-lg border border-[hsl(var(--border))]"
                        >
                          {/* Category Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <span className="text-gray-500 font-mono text-sm">#{idx + 1}</span>
                              <h3 className="text-white font-semibold">{cat.name}</h3>
                              <Badge className={`${mastery.color} text-white text-xs`}>
                                {mastery.level}
                              </Badge>
                            </div>
                            <div className="text-right">
                              <p className={`text-lg font-bold ${getAccuracyColor(cat.combined.accuracy)}`}>
                                {cat.combined.accuracy}%
                              </p>
                              <p className="text-gray-500 text-xs">{cat.combined.total} total</p>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="mb-3">
                            <Progress 
                              value={cat.combined.accuracy} 
                              className="h-2 bg-[hsl(var(--secondary))]"
                            />
                          </div>

                          {/* Mode Comparison */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 bg-[hsl(var(--secondary))] rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Folder className="w-4 h-4 text-violet-400" />
                                <span className="text-violet-400 text-sm font-medium">Daily Categories</span>
                              </div>
                              {cat.daily_categories.accuracy !== null ? (
                                <>
                                  <p className={`text-xl font-bold ${getAccuracyColor(cat.daily_categories.accuracy)}`}>
                                    {cat.daily_categories.accuracy}%
                                  </p>
                                  <p className="text-gray-500 text-xs">
                                    {cat.daily_categories.total} questions | {cat.daily_categories.sessions} sessions
                                  </p>
                                </>
                              ) : (
                                <p className="text-gray-500 text-sm">Not trained yet</p>
                              )}
                            </div>

                            <div className="p-3 bg-[hsl(var(--secondary))] rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Zap className="w-4 h-4 text-cyan-400" />
                                <span className="text-cyan-400 text-sm font-medium">Sprint Mode</span>
                              </div>
                              {cat.sprint.accuracy !== null ? (
                                <>
                                  <p className={`text-xl font-bold ${getAccuracyColor(cat.sprint.accuracy)}`}>
                                    {cat.sprint.accuracy}%
                                  </p>
                                  <p className="text-gray-500 text-xs">{cat.sprint.total} questions</p>
                                </>
                              ) : (
                                <p className="text-gray-500 text-sm">Not encountered</p>
                              )}
                            </div>
                          </div>

                          {/* Insight */}
                          {cat.insight && (
                            <div className="mt-3 flex items-center gap-2 text-sm">
                              {cat.daily_categories.accuracy > cat.sprint.accuracy ? (
                                <TrendingUp className="w-4 h-4 text-emerald-400" />
                              ) : cat.daily_categories.accuracy < cat.sprint.accuracy ? (
                                <TrendingDown className="w-4 h-4 text-amber-400" />
                              ) : (
                                <Minus className="w-4 h-4 text-gray-400" />
                              )}
                              <span className="text-gray-400">{cat.insight}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* RECENT SESSIONS TAB */}
          <TabsContent value="history" className="space-y-4">
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  Recent Sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.recent_sessions.map((session, idx) => (
                    <div 
                      key={session.session_id}
                      className="flex items-center justify-between p-4 bg-[hsl(var(--accent))] rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          session.accuracy >= 80 ? 'bg-emerald-500/20' : 
                          session.accuracy >= 60 ? 'bg-yellow-500/20' : 'bg-rose-500/20'
                        }`}>
                          {session.accuracy >= 80 ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                          ) : session.accuracy >= 60 ? (
                            <Target className="w-5 h-5 text-yellow-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-rose-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{session.category}</p>
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs ${getDifficultyColor(session.difficulty)}`}>
                              {session.difficulty}
                            </Badge>
                            <span className="text-gray-500 text-sm">{session.correct}/{session.total}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-xl font-bold ${getAccuracyColor(session.accuracy)}`}>
                          {session.accuracy}%
                        </p>
                        <p className="text-gray-500 text-xs">{formatDateFull(session.completed_at)}</p>
                      </div>
                    </div>
                  ))}

                  {stats.recent_sessions.length === 0 && (
                    <p className="text-gray-400 text-center py-8">No sessions recorded yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* REVIEW TAB - Questions from most recent session */}
          <TabsContent value="review" className="space-y-4">
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                  Review Most Recent Session
                </CardTitle>
                {stats.review_session && (
                  <p className="text-gray-400 text-sm">
                    {stats.review_session.category} &middot; {stats.review_session.difficulty} &middot; {formatDateFull(stats.review_session.completed_at)}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                {stats.review_questions && stats.review_questions.length > 0 ? (
                  <div className="space-y-4">
                    {stats.review_questions.map((q, idx) => (
                      <div 
                        key={q.question || `review-${idx}`} 
                        className={`p-4 rounded-lg bg-[hsl(var(--accent))] border-l-4 ${q.is_correct ? 'border-emerald-400' : 'border-rose-400'}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-[hsl(var(--brand-blue-800))] text-[hsl(var(--brand-gold-500))]">{q.category}</Badge>
                          {q.value && (
                            <Badge variant="outline" className="text-gray-400 border-gray-600">${q.value}</Badge>
                          )}
                          {q.is_correct ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />
                          ) : (
                            <XCircle className="w-4 h-4 text-rose-400 ml-auto" />
                          )}
                        </div>
                        <p className="text-white mb-3">{q.question}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-300">Your answer: </span>
                            <span className={q.is_correct ? 'text-emerald-400' : 'text-rose-400'}>
                              {q.player_answer || '(no answer)'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-300">Correct: </span>
                            <span className="text-emerald-400">{q.correct_answer}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Brain className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                    <p className="text-gray-300">No questions to review yet. Complete a session first!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
